import { type JSX,SyntheticEvent, useEffect, useState } from 'react';
import { Outlet, useLocation,useParams } from 'react-router-dom';

import { CircularProgress, Tabs,Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { NotFound } from 'common/components/NotFound';
import { Page } from 'common/components/Page';
import { selectPageInfo, setPageInfo } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ResourceDetailHeader } from 'common/components/ResourceDetailHeader';
import { StyledBreadcrumbs } from 'common/components/StyledBreadcrumbs';
import { StyledTab } from 'common/components/StyledTab';
import { Routes, RoutesNameMap } from 'common/constants/routes';
import { fetchAclAccesses } from 'common/services/api/Acls/AclAccesses.thunk';
import { fetchAclResources } from 'common/services/api/Acls/AclResources.thunk';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchUserGroupDetails, FetchUserGroupDetailsResponse } from 'common/services/api/UserGroups/UserGroups.thunk';
import { getLastPathSegment } from 'common/utils/locationHelpers';

export function UserGroupDetails(): JSX.Element {
    const [currentTab, setCurrentTab] = useState<Routes>(Routes.Users);
    const [processing, setProcessing] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    const location = useLocation();
    const params = useParams();

    useEffect(() => {
        const lastPathSegment = getLastPathSegment();
        setCurrentTab(lastPathSegment as Routes);
    }, [location]);

    const dispatch = useAppDispatch();
    useEffect(() => {
        const fetchParams = {
            usergroupId: params?.usergroupId ?? '',
            tenantId: params?.tenantId ?? '',
            applicationSuiteId: params?.suiteId ?? ''
        };
        dispatch(fetchUserGroupDetails(fetchParams))
            .unwrap()
            .then((result) => {
                dispatch(setPageInfo(result));
            })
            .catch((err) => {
                setError(err.message);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, []);

    useEffect(() => {
        const tenantId = params?.tenantId ?? '';
        dispatch(fetchAclAccesses({ tenantId })).catch(console.error);
        dispatch(fetchAclResources({ tenantId })).catch(console.error);
    }, []);

    const getTabLink = (key: string) => `/${Routes.ApplicationSuites}/${params.suiteId}/${Routes.Tenants}/${params.tenantId}/${Routes.UserGroups}/${params.usergroupId}/${key}`;
    const handleChangeTab = (_event: SyntheticEvent, newValue: Routes) => {
        setCurrentTab(newValue);
    };

    const userGroupDetails = useAppSelector(selectPageInfo) as FetchUserGroupDetailsResponse;
    const errorCode = extractStatusCode(error);

    if (errorCode === 404) {
        return <NotFound />;
    }

    if (errorCode && errorCode !== 200) {
        return <p>Something went wrong</p>;
    }

    if(processing) {
        return <CircularProgress color='primary'/>;
    }

    const DESCRIPTION_STYLES = {
        width: '100%',
        justifyContent: 'left',
        display: 'flex',
        alignItems: 'center'
    };

    return (
        <Page
            title={`${userGroupDetails?.name} - ${RoutesNameMap.get(Routes.UserGroup)}`}
            header={(
                <Typography variant="h5" gutterBottom component="div">
                    {userGroupDetails?.id &&
                        <StyledBreadcrumbs
                            guidNameMappings={[{
                                resourceId: params.suiteId ?? '',
                                resourceName: userGroupDetails?.applicationSuiteName || ''
                            }, {
                                resourceId: params.tenantId ?? '',
                                resourceName: userGroupDetails?.tenantName || ''
                            }, {
                                resourceId: params.usergroupId ?? '',
                                resourceName: userGroupDetails?.name || ''
                            }]}
                        /> }
                    {userGroupDetails?.id &&
                        <ResourceDetailHeader
                            resourceType='User Group'
                            resourceName={userGroupDetails?.name || ''}
                            resourceId={params.usergroupId ?? ''}
                        />
                    }
                    {userGroupDetails?.description &&
                        <Typography
                            variant="body1"
                            gutterBottom
                            component="div"
                            color="info.light"
                            sx={DESCRIPTION_STYLES}>
                            <span>User Group Description: {userGroupDetails.description}</span>
                        </Typography>
                    }
                </Typography>
            )}
        >
            <Tabs
                sx={{ mb: '-60px', zIndex:1200, width: '40rem' }}
                onChange={handleChangeTab}
                value={currentTab}
            >
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Users)}
                    key={Routes.Users}
                    link={getTabLink(Routes.Users)}
                    value={Routes.Users}
                />
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Roles)}
                    key={Routes.Roles}
                    link={getTabLink(Routes.Roles)}
                    value={Routes.Roles}
                />
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.AclEntries)}
                    key={Routes.AclEntries}
                    link={getTabLink(Routes.AclEntries)}
                    value={Routes.AclEntries}
                />
            </Tabs>
            <Outlet />
        </Page>
    );
}
